import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient, ROOT_URL } from '../App'

export const useGetExchangeRate = (params: {
  from: number | undefined
  to: number | undefined
}) => {
  return useQuery<{ data: { data: any } }>(
    [`/api/v1/wallets/:id`, params?.from, params?.to],
    async () => getExchangeRate(params),
    {
      enabled: !!params?.to && !!params?.from,
    }
  )
}

export function getExchangeRate(params: {
  from: number | undefined
  to: number | undefined
}) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/exchange-rates?_from=${params.from}&_to=${params.to}`
  )
}

export const useGetConfigurationFee = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getConfigurationFee,
  })
}

export function getConfigurationFee(beneficiaryCurrencyId: number | undefined) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/fee-configuration/currency/${beneficiaryCurrencyId}`
  )
}

export const useCreateSendMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: createSendMoney,
  })
}

export function createSendMoney(body: any) {
  return httpClient.post(`${ROOT_URL}/api/v1/payment_requests/create`, body)
}

export const useUpdateSendMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateSendMoney,
  })
}

export function updateSendMoney(params: any) {
  return httpClient.patch(`${ROOT_URL}/api/v1/payment_requests/update/${params?.id}`, params)
}

export const useGetDetailSendMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getDetailSendMoney,
  })
}

export const useGetOrderDetailSendMoney = (orderId: string | null) => {
  return useQuery<{ data: any }>(
    [`/api/v1/payment_requests/:id`, orderId],
    async () => getDetailSendMoney(orderId),
    {
      enabled: !!orderId,
    }
  )
}

export function getDetailSendMoney(orderId: any) {
  return httpClient.get(`${ROOT_URL}/api/v1/payment_requests/${orderId}`)
}

export const useGetListSendMoney = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _timeZone?: string
}) => {
  return useQuery<{ data: any }>(
    [
      `/api/v1/payment_requests/user/get-list`,
      params.start,
      params.end,
      params.name,
      params.status,
      params._gte,
      params._lte,
      params._timeZone,
    ],
    async () => getListSendMoney(params)
  )
}

export function getListSendMoney({
  start,
  end,
  name,
  status,
  _gte,
  _lte,
  _timeZone,
}: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _timeZone?: string
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (status) params.append('_status', status)
  if (_gte) params.append('_gte', _gte)
  if (_lte) params.append('_lte', _lte)
  if (_timeZone) params.append('_timeZone', _timeZone)

  return httpClient.get(
    `${ROOT_URL}/api/v1/payment_requests/user/get-list?${params.toString()}`
  )
}

export const useUpdateStatusSendMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateStatusSendMoney,
  })
}

export function updateStatusSendMoney(params: any) {
  return httpClient.patch(
    `${ROOT_URL}/api/v1/payment_requests/user/update/${params?.id}`,
    params
  )
}

export const useCreateRequestMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: createRequestMoney,
  })
}

export function createRequestMoney(body: any) {
  return httpClient.post(`${ROOT_URL}/api/v1/payment_orders/create`, body)
}

export const useGetDetailRequestMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: getDetailRequestMoney,
  })
}

export function getDetailRequestMoney(orderId: any) {
  return httpClient.get(`${ROOT_URL}/api/v1/payment_orders/${orderId}`)
}

export const useUpdateStatusRequestMoney = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateStatusRequestMoney,
  })
}

export function updateStatusRequestMoney(params: any) {
  return httpClient.patch(
    `${ROOT_URL}/api/v1/payment_orders/user/update/${params?.id}`,
    params
  )
}

export const useGetListRequestMoney = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _timeZone?: string
}) => {
  return useQuery<{ data: any }>(
    [
      `/api/v1/payment_orders/user/get-list`,
      params.start,
      params.end,
      params.name,
      params.status,
      params._gte,
      params._lte,
      params._timeZone,
    ],
    async () => getListRequestMoney(params)
  )
}

export function getListRequestMoney({
  start,
  end,
  name,
  status,
  _gte,
  _lte,
  _timeZone,
}: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _timeZone?: string
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (status) params.append('_status', status)
  if (_gte) params.append('_gte', _gte)
  if (_lte) params.append('_lte', _lte)
  if (_timeZone) params.append('_timeZone', _timeZone)

  return httpClient.get(
    `${ROOT_URL}/api/v1/payment_orders/user/get-list?${params.toString()}`
  )
}

export const useGetOrderDetailRequestMoney = (orderId: string) => {
  return useQuery<{ data: any }>(
    [`/api/v1/payment_requests/:id`, orderId],
    async () => getDetailRequestMoney(orderId),
    {
      enabled: !!orderId,
    }
  )
}

export const useGetListHistory = (params: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _timeZone?: string
  _historyType?: string
}) => {
  return useQuery<{ data: any }>(
    [
      `/api/v1/history/get-list`,
      params.start,
      params.end,
      params.name,
      params.status,
      params._gte,
      params._lte,
      params._timeZone,
      params._historyType,
    ],
    async () => getListHistory(params)
  )
}

export function getListHistory({
  start,
  end,
  name,
  status,
  _gte,
  _lte,
  _timeZone,
  _historyType,
}: {
  start: number
  end: number
  name?: string
  status?: string
  _gte?: string
  _lte?: string
  _timeZone?: string
  _historyType?: string
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (status) params.append('_status', status)
  if (_gte) params.append('_gte', _gte)
  if (_lte) params.append('_lte', _lte)
  if (_timeZone) params.append('_timeZone', _timeZone)
  if (_historyType) params.append('_historyType', _historyType)

  return httpClient.get(
    `${ROOT_URL}/api/v1/history/get-list?${params.toString()}`
  )
}
